import React, { useEffect, useState, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, Snackbar, Alert } from '@mui/material';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import AppHeader from './components/layout/AppHeader';
import BottomNav from './components/layout/BottomNav';
import ProtectedRoute from './components/auth/ProtectedRoute';
import theme from './styles/theme';
import ScrollToTop from './components/layout/ScrollToTop';
import { getRedirectPathForUser } from './utils/authRedirect';
import './index.css';
import { AUTH_MAIN_PADDING_BOTTOM_PX, AUTH_MAIN_PADDING_BOTTOM_COMPACT_PX } from './constants/layout';
import IdleTimeoutProvider from './components/auth/IdleTimeoutProvider';
import { JobSearchProvider } from './contexts/JobSearchContext';
import { BlogSearchProvider } from './contexts/BlogSearchContext';
import { MyInterviewsProvider } from './contexts/MyInterviewsContext';
import FcmBootstrap from './components/chat/FcmBootstrap';
import {
  RouteFallback,
  Home,
  About,
  HR,
  SuperAdmin,
  TechieAdminDashboard,
  HMAdminDashboard,
  CompanyAdminDashboard,
  SchoolAdminDashboard,
  MultiRoleAdminDashboard,
  Networking,
  NetworkFeed,
  MyNetwork,
  NetworkGroups,
  NetworkEvents,
  Combinator,
  Companies,
  CompanyRegistrationPage,
  Contact,
  Services,
  ServiceDetail,
  Login,
  Signup,
  TechieSignup,
  SignupSuccess,
  Verification,
  PostJob,
  TalentSearch,
  Advertise,
  Enterprise,
  ContactSales,
  Pricing,
  Support,
  Accessibility,
  CookiePolicy,
  PrivacyPolicy,
  TermsOfService,
  Terms,
  Privacy,
  Cookies,
  StatusProcessing,
  StatusAccepted,
  StatusRejected,
  ResetPassword,
  CompleteProfile,
  Admin,
  GitHubCallback,
  GitLabCallback,
  CalendarCallback,
  SuperAdminChat,
  CourseManagement,
  LearnAdmin,
  RequirementsAdminDashboard,
  ScreenerDashboard,
  TechScreenerDashboard,
  HmScreeningProgressPage,
  HRDashboard,
  CreateJobPost,
  ViewApplicants,
  JobListings,
  JobDetails,
  JobApply,
  CodingTest,
  MyApplications,
  TechieDashboard,
  CodingProblems,
  ProblemDetail,
  ProblemsPage,
  IDEPage,
  Chat,
  SchedulingPage,
  CalendarView,
  ProfilePage,
  Learn,
  Blogs,
  SearchResults,
  Notifications,
  CourseDetail,
  TutorialPage,
  QuizPage,
  VideoRoom,
  MeetingLobby,
  ScheduleInterview,
  MyInterviews,
  ProfileCompletion,
  PipelinePage,
  JobPostingsPage,
  AllCandidatesPage,
  CandidateProfilePage,
  InterviewsPage,
  ATSSchedulingPage,
  ATSCalendarPage,
  AnalyticsPage,
  HelpCenter,
  CustomerSupportDashboard,
  SMSPosts,
  SMSAlumni,
  SMSPrograms,
  SMSPlacements,
  SMSPageAdmins,
  SMSAnalytics,
  SMSSettings,
  CMSPosts,
  CMSEmployees,
  CMSAdmins,
  CMSAffiliatedUsers,
  CMSJobs,
  CMSAnalytics,
  CMSSettings,
  CMSMedia,
  CMSSnippets,
  PublicSchedulingPage,
} from './routing/lazyPages';

/**
 * PublicLayout - For unauthenticated pages (Home, About, Login, etc.)
 * If user has valid session (token + userData), redirect to role-based dashboard so
 * the public landing is never shown to logged-in users.
 */
const PublicLayout: React.FC = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  const userDataStr = typeof window !== 'undefined' ? localStorage.getItem('userData') : null;
  if (token && userDataStr) {
    try {
      const userData = JSON.parse(userDataStr);
      const path = getRedirectPathForUser(userData);
      if (path) return <Navigate to={path} replace />;
    } catch {
      // invalid userData, render public layout
    }
  }
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      background: theme.palette.background.gradient,
    }}>
      <Navbar />
      <Box component="main" sx={{
        flexGrow: 1,
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden'
      }}>
        <Outlet />
      </Box>
      <Footer />
    </Box>
  );
};

/**
 * AuthenticatedLayout - For logged-in users with role-based navigation
 * Includes: AppHeader (Toolbar xs 64px / md+ 70px) + BottomNav (fixed ~80px)
 * 
 * IMPORTANT: This layout handles ALL spacing for the fixed header and bottom nav.
 * Child pages should NOT add their own top/bottom padding for these elements.
 */
const AuthenticatedLayout: React.FC = () => {
  const location = useLocation();
  const [immersiveAssessmentMode, setImmersiveAssessmentMode] = useState(false);
  const isMeetingPage =
    location.pathname.startsWith('/techie/lobby/') ||
    location.pathname.startsWith('/techie/meet/');

  const isFullBleedChatRoute =
    location.pathname === '/techie/chat' ||
    location.pathname === '/hr/chat' ||
    location.pathname === '/super-admin/chat';

  /** CMS pages stack profile header + tabs + tables — need extra scroll inset above BottomNav */
  const isCmsRoute = location.pathname.startsWith('/techie/cms');

  const mainPaddingBottomPx = isMeetingPage || immersiveAssessmentMode
    ? 0
    : isFullBleedChatRoute
      ? AUTH_MAIN_PADDING_BOTTOM_COMPACT_PX
      : isCmsRoute
        ? AUTH_MAIN_PADDING_BOTTOM_PX + 72
        : AUTH_MAIN_PADDING_BOTTOM_PX;

  useEffect(() => {
    const handleAssessmentModeChange = (event: Event) => {
      const customEvent = event as CustomEvent<boolean>;
      setImmersiveAssessmentMode(Boolean(customEvent.detail));
    };

    window.addEventListener('vt-assessment-immersive-mode', handleAssessmentModeChange as EventListener);

    return () => {
      window.removeEventListener('vt-assessment-immersive-mode', handleAssessmentModeChange as EventListener);
    };
  }, []);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden',
      overflowX: 'hidden',
      /* Lock shell to viewport; <main> scrolls for long pages; full-height routes (e.g. chat) use flex:1 + minHeight:0 */
      height: '100dvh',
      maxHeight: '100dvh',
      minHeight: 0,
      background: isMeetingPage
        ? '#0a0a0f'
        : 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
      bgcolor: isMeetingPage ? '#0a0a0f' : '#f8fafc',
    }}>
      {!isMeetingPage && !immersiveAssessmentMode && <AppHeader />}
      <Box component="main" sx={{
        flex: '1 1 0%',
        width: '100%',
        maxWidth: '100vw',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        overflowY: isMeetingPage || immersiveAssessmentMode ? 'hidden' : 'auto',
        // Match AppHeader Toolbar + gap; add top safe area for notched devices
        pt: isMeetingPage || immersiveAssessmentMode
          ? 0
          : {
              xs: 'calc(80px + env(safe-area-inset-top, 0px))',
              md: 'calc(86px + env(safe-area-inset-top, 0px))',
            },
        pb: isMeetingPage || immersiveAssessmentMode
          ? 0
          : `calc(${mainPaddingBottomPx}px + env(safe-area-inset-bottom, 0px))`,
      }}>
        <Outlet />
      </Box>
      {!isMeetingPage && !immersiveAssessmentMode && <BottomNav />}
    </Box>
  );
};

// Cross-tab password reset listener component
const PasswordResetListener: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [showResetMessage, setShowResetMessage] = useState(false);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const lastResetTimeRef = useRef<number>(0);

  useEffect(() => {
    // Initialize BroadcastChannel listener
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      broadcastChannelRef.current = new BroadcastChannel('password-reset-channel');

      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'PASSWORD_RESET_COMPLETED') {
          const resetTime = event.data.timestamp || Date.now();
          // Avoid duplicate messages within 1 second
          if (resetTime - lastResetTimeRef.current > 1000) {
            lastResetTimeRef.current = resetTime;
            handlePasswordResetCompleted();
          }
        }
      };

      broadcastChannelRef.current.addEventListener('message', handleMessage);

      return () => {
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.removeEventListener('message', handleMessage);
          broadcastChannelRef.current.close();
        }
      };
    }
  }, []);

  // Also listen to localStorage changes (fallback for older browsers)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'passwordResetCompleted' && e.newValue) {
        const resetTime = parseInt(e.newValue, 10);
        if (resetTime - lastResetTimeRef.current > 1000) {
          lastResetTimeRef.current = resetTime;
          handlePasswordResetCompleted();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Check if reset was completed while this tab was inactive
    const checkResetStatus = () => {
      const resetTimeStr = localStorage.getItem('passwordResetCompleted');
      if (resetTimeStr) {
        const resetTime = parseInt(resetTimeStr, 10);
        const now = Date.now();
        // If reset happened in last 5 seconds, show message
        if (resetTime && now - resetTime < 5000 && resetTime - lastResetTimeRef.current > 1000) {
          lastResetTimeRef.current = resetTime;
          handlePasswordResetCompleted();
        }
      }
    };

    // Check on focus
    window.addEventListener('focus', checkResetStatus);
    checkResetStatus(); // Initial check

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', checkResetStatus);
    };
  }, []);

  const handlePasswordResetCompleted = () => {
    // Clear auth tokens since password was reset
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('accessToken');
    // Clean up the reset completion flag after a delay
    setTimeout(() => {
      localStorage.removeItem('passwordResetCompleted');
    }, 10000);

    // Show message
    setShowResetMessage(true);

    // Redirect to login after showing message (unless already on login/reset-password page)
    const currentPath = window.location.pathname;
    if (currentPath !== '/login' && currentPath !== '/reset-password') {
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  };

  return (
    <>
      {children}
      <Snackbar
        open={showResetMessage}
        autoHideDuration={5000}
        onClose={() => setShowResetMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowResetMessage(false)}
          severity="info"
          sx={{ width: '100%' }}
        >
          Password reset completed. Please login again.
        </Alert>
      </Snackbar>
    </>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <PasswordResetListener>
      <IdleTimeoutProvider>
        <ScrollToTop />
        <FcmBootstrap />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* ========== PUBLIC ROUTES ========== */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/hr" element={<HR />} />
            <Route path="/networking" element={<Networking />} />
            <Route path="/companies" element={<Companies />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:id" element={<ServiceDetail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup/company" element={<CompanyRegistrationPage />} />
            <Route path="/techie-signup" element={<TechieSignup />} />
            <Route path="/signup-success" element={<SignupSuccess />} />
            <Route path="/verification" element={<Verification />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />

            {/* Status Routes */}
            <Route path="/status/processing" element={<StatusProcessing />} />
            <Route path="/status/accepted" element={<StatusAccepted />} />
            <Route path="/status/rejected" element={<StatusRejected />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* GitHub OAuth Callback */}
            <Route path="/github/callback" element={<GitHubCallback />} />
            {/* GitLab OAuth Callback */}
            <Route path="/gitlab/callback" element={<GitLabCallback />} />

            {/* Profile Completion Route */}
            <Route path="/complete-profile" element={<CompleteProfile />} />

            {/* Company Info Routes */}
            <Route path="/companies/post-job" element={<PostJob />} />
            <Route path="/companies/talent-search" element={<TalentSearch />} />
            <Route path="/companies/advertise" element={<Advertise />} />
            <Route path="/companies/enterprise" element={<Enterprise />} />
            <Route path="/companies/contact-sales" element={<ContactSales />} />
            <Route path="/pricing" element={<Pricing />} />

            {/* Public Jobs */}
            <Route path="/jobs" element={<JobListings />} />
            <Route path="/jobs/:jobId" element={<JobDetails />} />

            {/* Legal & Support */}
            <Route path="/support" element={<Support />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />

            {/* Redirect /admin to /super-admin */}
            <Route path="/admin" element={<Navigate to="/super-admin" replace />} />
          </Route>

          {/* ========== AUTHENTICATED ROUTES ========== */}
          <Route element={<AuthenticatedLayout />}>

            {/* ========== ADMIN ROUTES (Under /vertechie/) ========== */}

            {/* Super Admin - Full Platform Control */}
            <Route path="/vertechie/super-admin" element={
              <ProtectedRoute requiredRole="superadmin">
                <SuperAdmin />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/super-admin/*" element={
              <ProtectedRoute requiredRole="superadmin">
                <SuperAdmin />
              </ProtectedRoute>
            } />

            {/* Company Admin - Manages Company Registrations & Approvals */}
            <Route path="/vertechie/companyadmin" element={
              <ProtectedRoute requiredRole="admin">
                <CompanyAdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/companyadmin/*" element={
              <ProtectedRoute requiredRole="admin">
                <CompanyAdminDashboard />
              </ProtectedRoute>
            } />

            {/* School Admin - Manages School Registrations & Approvals */}
            <Route path="/vertechie/schooladmin" element={
              <ProtectedRoute requiredRole="admin">
                <SchoolAdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/schooladmin/*" element={
              <ProtectedRoute requiredRole="admin">
                <SchoolAdminDashboard />
              </ProtectedRoute>
            } />

            {/* Techie Admin - Manages Tech Professional Registrations & Approvals */}
            <Route path="/vertechie/techieadmin" element={
              <ProtectedRoute requiredRole="admin">
                <TechieAdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/techieadmin/*" element={
              <ProtectedRoute requiredRole="admin">
                <TechieAdminDashboard />
              </ProtectedRoute>
            } />

            {/* Hiring Manager Admin - Manages HM Registrations & Approvals */}
            <Route path="/vertechie/hiringmanageradmin" element={
              <ProtectedRoute requiredRole="admin">
                <HMAdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/hiringmanageradmin/*" element={
              <ProtectedRoute requiredRole="admin">
                <HMAdminDashboard />
              </ProtectedRoute>
            } />
            {/* Short alias for HM Admin */}
            <Route path="/vertechie/hmadmin" element={
              <ProtectedRoute requiredRole="admin">
                <HMAdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/hmadmin/*" element={
              <ProtectedRoute requiredRole="admin">
                <HMAdminDashboard />
              </ProtectedRoute>
            } />

            {/* Multi-Role Admin - One screen with tabs for each role (techie_admin, hm_admin, etc.) */}
            <Route path="/vertechie/role-admin" element={
              <ProtectedRoute requiredRole="admin">
                <MultiRoleAdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/role-admin/*" element={
              <ProtectedRoute requiredRole="admin">
                <MultiRoleAdminDashboard />
              </ProtectedRoute>
            } />

            {/* BDM company queue lives under VerTechie Admin → User Management → Company registration (BDM) */}
            <Route path="/vertechie/bdmadmin" element={
              <ProtectedRoute requiredRole="admin">
                <Navigate to="/vertechie/admin?bdm=1" replace />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/bdmadmin/*" element={
              <ProtectedRoute requiredRole="admin">
                <Navigate to="/vertechie/admin?bdm=1" replace />
              </ProtectedRoute>
            } />

            {/* Multi-Role Admin - For admins with multiple roles */}
            <Route path="/vertechie/admin" element={
              <ProtectedRoute requiredRole="admin">
                <Admin />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/admin/*" element={
              <ProtectedRoute requiredRole="admin">
                <Admin />
              </ProtectedRoute>
            } />

            {/* Learn Admin - Complete Course Management System */}
            <Route path="/vertechie/learn-admin" element={
              <ProtectedRoute requiredRole="admin">
                <LearnAdmin />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/learn-admin/*" element={
              <ProtectedRoute requiredRole="admin">
                <LearnAdmin />
              </ProtectedRoute>
            } />

            <Route path="/vertechie/requirementsadmin" element={
              <ProtectedRoute requiredRole="screening">
                <RequirementsAdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/requirementsadmin/*" element={
              <ProtectedRoute requiredRole="screening">
                <RequirementsAdminDashboard />
              </ProtectedRoute>
            } />

            <Route path="/vertechie/screeneradmin" element={
              <ProtectedRoute requiredRole="screening">
                <ScreenerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/screeneradmin/*" element={
              <ProtectedRoute requiredRole="screening">
                <ScreenerDashboard />
              </ProtectedRoute>
            } />

            <Route path="/vertechie/techscreeneradmin" element={
              <ProtectedRoute requiredRole="screening">
                <TechScreenerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/techscreeneradmin/*" element={
              <ProtectedRoute requiredRole="screening">
                <TechScreenerDashboard />
              </ProtectedRoute>
            } />

            <Route path="/vertechie/courses" element={
              <ProtectedRoute requiredRole="admin">
                <LearnAdmin />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/course-management" element={
              <ProtectedRoute requiredRole="admin">
                <LearnAdmin />
              </ProtectedRoute>
            } />

            {/* Legacy Course Management (Simple Version) */}
            <Route path="/vertechie/course-simple" element={
              <ProtectedRoute requiredRole="admin">
                <CourseManagement />
              </ProtectedRoute>
            } />

            {/* Customer Support Dashboard */}
            <Route path="/vertechie/support" element={
              <ProtectedRoute requiredRole="support">
                <CustomerSupportDashboard />
              </ProtectedRoute>
            } />
            <Route path="/vertechie/support/tickets/:ticketId" element={
              <ProtectedRoute requiredRole="support">
                <CustomerSupportDashboard />
              </ProtectedRoute>
            } />

            {/* Legacy routes - redirect to new structure */}
            <Route path="/super-admin" element={
              <ProtectedRoute requiredRole="superadmin">
                <SuperAdmin />
              </ProtectedRoute>
            } />
            <Route path="/super-admin/*" element={
              <ProtectedRoute requiredRole="superadmin">
                <SuperAdmin />
              </ProtectedRoute>
            } />
            <Route path="/super-admin/chat" element={
              <ProtectedRoute requiredRole="superadmin">
                <SuperAdminChat />
              </ProtectedRoute>
            } />

            {/* HR Routes */}
            <Route path="/hr/dashboard" element={<HRDashboard />} />
            <Route path="/hr/feed" element={<Navigate to="/techie/home/feed" replace />} />
            <Route path="/hr/jobs" element={<HRDashboard />} />
            <Route path="/hr/candidates" element={<HRDashboard />} />
            <Route path="/hr/interviews" element={
              <ProtectedRoute requiredRole="hr">
                <InterviewsPage />
              </ProtectedRoute>
            } />
            <Route path="/hr/interviews/calendar" element={
              <ProtectedRoute requiredRole="hr">
                <ATSCalendarPage />
              </ProtectedRoute>
            } />
            <Route path="/hr/analytics" element={
              <ProtectedRoute requiredRole="hr">
                <AnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="/hr/chat" element={<Chat />} />
            <Route path="/hr/alerts" element={<Notifications />} />
            <Route path="/hr/profile" element={<ProfilePage />} />
            <Route path="/hr/saved" element={<HRDashboard />} />
            <Route path="/hr/settings" element={<HRDashboard />} />
            <Route path="/hr/help" element={
              <ProtectedRoute requiredRole="hr">
                <HelpCenter />
              </ProtectedRoute>
            } />
            <Route path="/hr/create-job" element={<CreateJobPost />} />
            <Route path="/hr/job/:jobId/edit" element={<CreateJobPost />} />
            <Route path="/hr/job/:jobId/applicants" element={<ViewApplicants />} />

            {/* Hiring Manager Routes */}
            <Route path="/hm/dashboard" element={<HRDashboard />} />
            <Route path="/hm/jobs" element={<HRDashboard />} />
            <Route path="/hm/candidates" element={<HRDashboard />} />
            <Route path="/hm/assessments" element={<HRDashboard />} />
            <Route path="/hm/interviews" element={
              <ProtectedRoute requiredRole="hr">
                <InterviewsPage />
              </ProtectedRoute>
            } />
            <Route path="/hm/interviews/calendar" element={
              <ProtectedRoute requiredRole="hr">
                <ATSCalendarPage />
              </ProtectedRoute>
            } />
            <Route path="/hm/analytics" element={
              <ProtectedRoute requiredRole="hr">
                <AnalyticsPage />
              </ProtectedRoute>
            } />

            {/* User Applications */}
            <Route path="/jobs/:jobId/apply" element={
              <ProtectedRoute requiredRole="user">
                <JobApply />
              </ProtectedRoute>
            } />
            <Route path="/my-applications" element={
              <ProtectedRoute requiredRole="user">
                <MyApplications />
              </ProtectedRoute>
            } />

            {/* Help Center — all authenticated users */}
            <Route path="/help" element={
              <ProtectedRoute requiredRole="user">
                <HelpCenter />
              </ProtectedRoute>
            } />
            <Route path="/help/tickets/:ticketId" element={
              <ProtectedRoute requiredRole="user">
                <HelpCenter />
              </ProtectedRoute>
            } />

            {/* ========== TECHIE PLATFORM ROUTES ========== */}
            <Route path="/techie" element={
              <Navigate to="/techie/home/feed" replace />
            } />
            <Route path="/techie/dashboard" element={
              <ProtectedRoute requiredRole="user">
                <TechieDashboard />
              </ProtectedRoute>
            } />

            {/* Jobs Section */}
            <Route path="/techie/jobs" element={
              <ProtectedRoute requiredRole="user">
                <JobListings />
              </ProtectedRoute>
            } />
            <Route path="/techie/jobs/:jobId" element={
              <ProtectedRoute requiredRole="user">
                <JobDetails />
              </ProtectedRoute>
            } />
            <Route path="/techie/jobs/:jobId/apply" element={
              <ProtectedRoute requiredRole="user">
                <JobApply />
              </ProtectedRoute>
            } />
            <Route path="/techie/jobs/:jobId/coding-test" element={
              <ProtectedRoute requiredRole="user">
                <CodingTest />
              </ProtectedRoute>
            } />
            <Route path="/techie/my-applications" element={
              <ProtectedRoute requiredRole="user">
                <MyApplications />
              </ProtectedRoute>
            } />

            {/* Practice Section */}
            <Route path="/techie/practice" element={
              <ProtectedRoute requiredRole="user">
                <ProblemsPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/problems" element={
              <ProtectedRoute requiredRole="user">
                <ProblemsPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/problems/:slug" element={
              <ProtectedRoute requiredRole="user">
                <ProblemDetail />
              </ProtectedRoute>
            } />
            <Route path="/techie/coding-problems" element={
              <ProtectedRoute requiredRole="user">
                <CodingProblems />
              </ProtectedRoute>
            } />
            <Route path="/techie/contests" element={
              <ProtectedRoute requiredRole="user">
                <TechieDashboard />
              </ProtectedRoute>
            } />

            {/* Network is now the Home page - redirects handled below */}
            <Route path="/techie/network" element={
              <Navigate to="/techie/home" replace />
            } />
            <Route path="/techie/network/*" element={
              <Navigate to="/techie/home" replace />
            } />

            {/* Community Section - Redirects to Network pages */}
            <Route path="/techie/community" element={
              <Navigate to="/techie/home/feed" replace />
            } />
            <Route path="/techie/groups" element={
              <Navigate to="/techie/home/groups" replace />
            } />
            <Route path="/techie/feed" element={
              <Navigate to="/techie/home/feed" replace />
            } />

            {/* Learn Section */}
            <Route path="/techie/learn" element={
              <ProtectedRoute requiredRole="user">
                <Learn />
              </ProtectedRoute>
            } />
            <Route path="/techie/learn/tutorial/:tutorialSlug" element={
              <ProtectedRoute requiredRole="user">
                <TutorialPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/learn/tutorial/:tutorialSlug/:lessonSlug" element={
              <ProtectedRoute requiredRole="user">
                <TutorialPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/learn/quiz/:tutorialSlug/:lessonSlug" element={
              <ProtectedRoute requiredRole="user">
                <QuizPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/learn/quiz/:tutorialSlug" element={
              <ProtectedRoute requiredRole="user">
                <QuizPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/learn/:courseId" element={
              <ProtectedRoute requiredRole="user">
                <CourseDetail />
              </ProtectedRoute>
            } />
            <Route path="/techie/learn/:courseId/lesson/:lessonId" element={
              <ProtectedRoute requiredRole="user">
                <CourseDetail />
              </ProtectedRoute>
            } />
            <Route path="/techie/learning" element={
              <ProtectedRoute requiredRole="user">
                <Learn />
              </ProtectedRoute>
            } />

            {/* Other Techie Pages */}
            <Route path="/techie/achievements" element={
              <ProtectedRoute requiredRole="user">
                <TechieDashboard />
              </ProtectedRoute>
            } />
            <Route path="/techie/messages" element={
              <ProtectedRoute requiredRole="user">
                <TechieDashboard />
              </ProtectedRoute>
            } />
            <Route path="/techie/assessments" element={
              <ProtectedRoute requiredRole="user">
                <TechieDashboard />
              </ProtectedRoute>
            } />

            {/* Chat Section */}
            <Route path="/techie/chat" element={
              <ProtectedRoute requiredRole="user">
                <Chat />
              </ProtectedRoute>
            } />

            {/* Home - Network Hub with separate pages for each section */}
            <Route path="/techie/home" element={
              <Navigate to="/techie/home/feed" replace />
            } />
            <Route path="/techie/home/feed" element={
              <ProtectedRoute requiredRole="user">
                <NetworkFeed />
              </ProtectedRoute>
            } />
            <Route path="/techie/home/network" element={
              <ProtectedRoute requiredRole="user">
                <MyNetwork />
              </ProtectedRoute>
            } />
            <Route path="/techie/home/groups" element={
              <ProtectedRoute requiredRole="user">
                <NetworkGroups />
              </ProtectedRoute>
            } />
            <Route path="/techie/home/events" element={
              <ProtectedRoute requiredRole="user">
                <NetworkEvents />
              </ProtectedRoute>
            } />
            <Route path="/techie/home/combinator" element={
              <ProtectedRoute requiredRole="user">
                <Combinator />
              </ProtectedRoute>
            } />

            {/* Create Company/School Pages (for Tech Professionals & Hiring Managers) */}
            <Route path="/techie/create-company" element={
              <ProtectedRoute requiredRole="user">
                <CompanyRegistrationPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/create-school" element={
              <ProtectedRoute requiredRole="user">
                <TechieDashboard />
              </ProtectedRoute>
            } />

            {/* User Settings & Saved */}
            <Route path="/techie/settings" element={
              <ProtectedRoute requiredRole="user">
                <TechieDashboard />
              </ProtectedRoute>
            } />
            <Route path="/techie/saved" element={
              <ProtectedRoute requiredRole="user">
                <TechieDashboard />
              </ProtectedRoute>
            } />
            <Route path="/techie/help" element={
              <ProtectedRoute requiredRole="user">
                <HelpCenter />
              </ProtectedRoute>
            } />

            {/* ========== USER-SIDE MANAGEMENT PAGES ========== */}

            {/* ATS - Applicant Tracking System (Separate Pages for Better Usability) */}
            <Route path="/techie/ats" element={
              <ProtectedRoute requiredRole="user">
                <PipelinePage />
              </ProtectedRoute>
            } />
            <Route path="/techie/ats/pipeline" element={
              <ProtectedRoute requiredRole="user">
                <PipelinePage />
              </ProtectedRoute>
            } />
            <Route path="/techie/ats/jobpostings" element={
              <ProtectedRoute requiredRole="user">
                <JobPostingsPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/ats/screening-progress" element={
              <ProtectedRoute requiredRole="user">
                <HmScreeningProgressPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/ats/allcandidates" element={
              <ProtectedRoute requiredRole="user">
                <AllCandidatesPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/ats/hired" element={
              <ProtectedRoute requiredRole="user">
                <AllCandidatesPage initialStageFilter="hired" />
              </ProtectedRoute>
            } />
            <Route path="/techie/ats/rejected" element={
              <ProtectedRoute requiredRole="user">
                <AllCandidatesPage initialStageFilter="rejected" />
              </ProtectedRoute>
            } />
            <Route path="/techie/ats/candidate/:candidateId" element={
              <ProtectedRoute requiredRole="user">
                <CandidateProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/techie/ats/interviews" element={
              <ProtectedRoute requiredRole="user">
                <InterviewsPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/ats/scheduling" element={
              <ProtectedRoute requiredRole="user">
                <ATSSchedulingPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/ats/calendar" element={
              <ProtectedRoute requiredRole="user">
                <ATSCalendarPage />
              </ProtectedRoute>
            } />
            <Route path="/calendar/callback/google" element={
              <ProtectedRoute requiredRole="user">
                <CalendarCallback provider="google" />
              </ProtectedRoute>
            } />
            <Route path="/calendar/callback/microsoft" element={
              <ProtectedRoute requiredRole="user">
                <CalendarCallback provider="microsoft" />
              </ProtectedRoute>
            } />
            <Route path="/techie/ats/analytics" element={
              <ProtectedRoute requiredRole="user">
                <AnalyticsPage />
              </ProtectedRoute>
            } />

            {/* Video Conferencing Routes */}
            <Route path="/techie/lobby/interview-:roomId" element={
              <ProtectedRoute requiredRole="user">
                <MeetingLobby />
              </ProtectedRoute>
            } />
            <Route path="/techie/lobby/:roomId" element={
              <ProtectedRoute requiredRole="user">
                <MeetingLobby />
              </ProtectedRoute>
            } />
            <Route path="/techie/meet/:roomId" element={
              <ProtectedRoute requiredRole="user">
                <VideoRoom />
              </ProtectedRoute>
            } />
            <Route path="/techie/schedule-interview" element={
              <ProtectedRoute requiredRole="user">
                <ScheduleInterview />
              </ProtectedRoute>
            } />

            {/* My Interviews - For Techies to see their scheduled interviews */}
            <Route path="/techie/my-interviews" element={
              <ProtectedRoute requiredRole="user">
                <MyInterviews />
              </ProtectedRoute>
            } />

            {/* Scheduling - Calendar & Scheduling (Calendly-like) */}
            <Route path="/techie/scheduling" element={
              <ProtectedRoute requiredRole="user">
                <SchedulingPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/scheduling/*" element={
              <ProtectedRoute requiredRole="user">
                <SchedulingPage />
              </ProtectedRoute>
            } />

            {/* Calendar View - Full Calendar with Events */}
            <Route path="/techie/calendar" element={
              <ProtectedRoute requiredRole="user">
                <CalendarView />
              </ProtectedRoute>
            } />
            <Route path="/techie/calendar/*" element={
              <ProtectedRoute requiredRole="user">
                <CalendarView />
              </ProtectedRoute>
            } />

            {/* SMS - School Page Management (for School Page Owners - like LinkedIn page management) */}
            {/* SMS - School Management System (Separate Pages) */}
            <Route path="/techie/sms" element={<Navigate to="/techie/sms/posts" replace />} />
            <Route path="/techie/sms/posts" element={
              <ProtectedRoute requiredRole="user">
                <SMSPosts />
              </ProtectedRoute>
            } />
            <Route path="/techie/sms/alumni" element={
              <ProtectedRoute requiredRole="user">
                <SMSAlumni />
              </ProtectedRoute>
            } />
            <Route path="/techie/sms/programs" element={
              <ProtectedRoute requiredRole="user">
                <SMSPrograms />
              </ProtectedRoute>
            } />
            <Route path="/techie/sms/placements" element={
              <ProtectedRoute requiredRole="user">
                <SMSPlacements />
              </ProtectedRoute>
            } />
            <Route path="/techie/sms/admins" element={
              <ProtectedRoute requiredRole="user">
                <SMSPageAdmins />
              </ProtectedRoute>
            } />
            <Route path="/techie/sms/analytics" element={
              <ProtectedRoute requiredRole="user">
                <SMSAnalytics />
              </ProtectedRoute>
            } />
            <Route path="/techie/sms/settings" element={
              <ProtectedRoute requiredRole="user">
                <SMSSettings />
              </ProtectedRoute>
            } />

            {/* CMS - Company Management System (Separate Pages) */}
            <Route path="/techie/cms" element={<Navigate to="/techie/cms/posts" replace />} />
            <Route path="/techie/cms/posts" element={
              <ProtectedRoute requiredRole="user">
                <CMSPosts />
              </ProtectedRoute>
            } />
            <Route path="/techie/cms/employees" element={
              <ProtectedRoute requiredRole="user">
                <CMSEmployees />
              </ProtectedRoute>
            } />
            <Route path="/techie/cms/admins" element={
              <ProtectedRoute requiredRole="user">
                <CMSAdmins />
              </ProtectedRoute>
            } />
            <Route path="/techie/cms/affiliated-users" element={
              <ProtectedRoute requiredRole="user">
                <CMSAffiliatedUsers />
              </ProtectedRoute>
            } />
            <Route path="/techie/cms/jobs" element={
              <ProtectedRoute requiredRole="user">
                <CMSJobs />
              </ProtectedRoute>
            } />
            <Route path="/techie/cms/analytics" element={
              <ProtectedRoute requiredRole="user">
                <CMSAnalytics />
              </ProtectedRoute>
            } />
            <Route path="/techie/cms/settings" element={
              <ProtectedRoute requiredRole="user">
                <CMSSettings />
              </ProtectedRoute>
            } />
            <Route path="/techie/cms/media" element={
              <ProtectedRoute requiredRole="user">
                <CMSMedia />
              </ProtectedRoute>
            } />
            <Route path="/techie/cms/snippets" element={
              <ProtectedRoute requiredRole="user">
                <CMSSnippets />
              </ProtectedRoute>
            } />

            {/* Enterprise IDE Routes */}
            <Route path="/techie/ide" element={
              <ProtectedRoute requiredRole="user">
                <IDEPage />
              </ProtectedRoute>
            } />
            <Route path="/techie/ide/:projectId" element={
              <ProtectedRoute requiredRole="user">
                <IDEPage />
              </ProtectedRoute>
            } />

            {/* Blog (authenticated view) */}
            <Route path="/techie/blogs" element={
              <ProtectedRoute requiredRole="user">
                <Blogs />
              </ProtectedRoute>
            } />

            {/* Search Results */}
            <Route path="/techie/search" element={
              <ProtectedRoute requiredRole="user">
                <SearchResults />
              </ProtectedRoute>
            } />

            {/* Alerts/Notifications */}
            <Route path="/techie/alerts" element={
              <ProtectedRoute requiredRole="user">
                <Notifications />
              </ProtectedRoute>
            } />

            {/* Profile Completion (First Login After Approval) */}
            <Route path="/techie/profile-completion" element={
              <ProtectedRoute requiredRole="user">
                <ProfileCompletion />
              </ProtectedRoute>
            } />

            {/* User Profile & Settings */}
            <Route path="/techie/profile" element={
              <ProtectedRoute requiredRole="user">
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/techie/profile/:userId" element={
              <ProtectedRoute requiredRole="user">
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute requiredRole="user">
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/profile/:userId" element={
              <ProtectedRoute requiredRole="user">
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute requiredRole="user">
                <TechieDashboard />
              </ProtectedRoute>
            } />
            <Route path="/saved" element={
              <ProtectedRoute requiredRole="user">
                <TechieDashboard />
              </ProtectedRoute>
            } />
          </Route>

          {/* Public Scheduling Pages - No Authentication Required */}
          <Route path="/schedule/:username" element={<PublicSchedulingPage />} />
          <Route path="/book/:linkId" element={<PublicSchedulingPage />} />
        </Routes>
        </Suspense>
      </IdleTimeoutProvider>
    </PasswordResetListener>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden' }}>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <JobSearchProvider>
          <BlogSearchProvider>
          <MyInterviewsProvider>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden' }}>
            <AppRoutes />
          </Box>
          </MyInterviewsProvider>
          </BlogSearchProvider>
          </JobSearchProvider>
        </Router>
      </Box>
    </ThemeProvider>
  );
};

export default App;
